import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CustomPart } from "@onsemble/shared";
import { Plus } from "lucide-react";
import { formatCentsAsDollars } from "@/lib/price-utils";

interface CustomPartSelectorProps {
  customParts: CustomPart[];
  selectedCustomParts: string[];
  onCustomPartSelect: (customPart: CustomPart) => void;
  onAddNewCustomPart: () => void;
  disabled?: boolean;
}

export const CustomPartSelector: React.FC<CustomPartSelectorProps> = ({
  customParts,
  selectedCustomParts,
  onCustomPartSelect,
  onAddNewCustomPart,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<string>("");

  // Show all custom parts but we'll indicate selected ones
  const availableCustomParts = customParts;

  const handleValueChange = (value: string) => {
    if (value === "add-new") {
      onAddNewCustomPart();
      setSelectedValue(""); // Reset selection
    } else if (value && value !== "") {
      const customPart = customParts.find((cp) => cp.uid === value);
      if (customPart) {
        onCustomPartSelect(customPart);
      }
      setSelectedValue(""); // Reset selection after adding
    }
  };

  return (
    <Select
      value={selectedValue}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger className="w-[200px] text-sm">
        <SelectValue
          placeholder={
            selectedCustomParts.length > 0
              ? `Custom part (${selectedCustomParts.length})`
              : t("components.customParts.customPartSelector.selectCustomPart")
          }
        />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="add-new" className="text-blue-600 font-medium">
          <div className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            {t("components.customParts.customPartSelector.addNewCustomPart")}
          </div>
        </SelectItem>
        {availableCustomParts.length > 0 ? (
          availableCustomParts.map((customPart) => {
            const isSelected = selectedCustomParts.includes(customPart.uid);
            return (
              <SelectItem key={customPart.uid} value={customPart.uid}>
                <div className="flex items-center justify-between w-full">
                  <span className={isSelected ? "text-muted-foreground" : ""}>
                    {customPart.title} {isSelected && "✓"}
                  </span>
                  <span className="text-muted-foreground ml-2">
                    ${formatCentsAsDollars(customPart.cost)}
                  </span>
                </div>
              </SelectItem>
            );
          })
        ) : (
          <SelectItem value="no-parts" disabled>
            {t("components.customParts.customPartSelector.noCustomPartsAvailable")}
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
};
